import React, { useEffect, useState } from 'react';
import { MessageCircle, Calendar, MapPin, Instagram, Send, Facebook, Star, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import ReservationForm from '../components/ReservationForm';

const Home = () => {
  const [userName, setUserName] = useState('');

  // Get user name from localStorage if exists
  useEffect(() => {
    const savedData = localStorage.getItem('saori_reservation');
    if (savedData) {
      const data = JSON.parse(savedData);
      setUserName(data.nombre);
    }
  }, []);

  const whatsappNumber = '5491163585211';

  // Mensaje prellenado de WhatsApp actualizado
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(`Hola, vengo de la página de Saori.

Quisiera consultar disponibilidad para un vestido.

Estoy buscando vestido de:
Fiesta / Novia / XV

Mi evento es el día:`);
    const whatsappLink = `https://wa.me/${whatsappNumber}?text=${message}`;

    // Trigger Meta Pixel Contact event
    if (window.fbq) {
      window.fbq('track', 'Contact');
    }

    window.open(whatsappLink, '_blank');
  };

  // Redirección a saori.ar para ver más modelos
  const handleVerMasModelos = () => {
    window.open('https://saori.ar', '_blank');
  };

  // Imágenes de la galería organizadas por categoría
  const galleryImages = {
    fiesta: [
      'https://customer-assets.emergentagent.com/job_saori-boutique/artifacts/96i2w7be_1.JPG',
      'https://customer-assets.emergentagent.com/job_saori-boutique/artifacts/wjktyglm_2.JPG',
      'https://customer-assets.emergentagent.com/job_saori-boutique/artifacts/fxw2cukd_3.JPG',
    ],
    novia: [
      'https://customer-assets.emergentagent.com/job_saori-boutique/artifacts/4sii70qx_6.JPG',
      'https://customer-assets.emergentagent.com/job_8f6bde00-e46b-4a3c-9f2d-fe927c3f2a5f/artifacts/s2ecmgjf_image.png',
      'https://customer-assets.emergentagent.com/job_8f6bde00-e46b-4a3c-9f2d-fe927c3f2a5f/artifacts/04xj1iwk_7.jpg',
    ],
    xv: [
      'https://customer-assets.emergentagent.com/job_saori-boutique/artifacts/tud3t45i_8.jpg',
      'https://customer-assets.emergentagent.com/job_saori-boutique/artifacts/ehjh57yn_9.jpg'
    ]
  };

  const steps = [
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: 'Nos escribís por WhatsApp',
      description: 'Coordinamos día y horario'
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: 'Te asesoramos en el showroom',
      description: 'Probás y reservás tu vestido ideal'
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: 'Sin compromiso de reserva',
      description: 'Elegí con tranquilidad'
    }
  ];

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);

    // Trigger Meta Pixel PageView
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, []);

  return (
    <div className="home-container">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <img
            src="https://customer-assets.emergentagent.com/job_2ad91963-089e-4db0-b314-e1367663a960/artifacts/ccgaqtts_saori%20sin%20fondo.png"
            alt="SAORI"
            className="logo" />
        </div>
      </header>

      {/* BLOQUE 1 - HERO (ACTUALIZADO) */}
      <section className="hero-section">
        {/* Banner de beneficios */}
        <div className="benefits-banner">
          <p className="benefits-text">
            Promociones activas para reservas 2026 – Cupos limitados
          </p>
        </div>

        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Reservá hoy tu vestido para tu evento
              <span className="hero-title-subtitle">Fiesta · Novias · XV</span>
            </h1>
            <p className="hero-location">Showroom privado en Morón</p>
            <p className="hero-urgency">
              Novias y XV suelen reservar con 6 meses de anticipación<br/>
              Vestidos de fiesta con 2 meses
            </p>
            <Button
              onClick={handleWhatsAppClick}
              className="hero-cta"
              size="lg"
              data-testid="hero-whatsapp-btn">
              <MessageCircle className="mr-2 h-5 w-5" />
              Consultar disponibilidad por WhatsApp
            </Button>
            <p className="hero-microcopy">
              Respuesta rápida · Atención personalizada
            </p>
          </div>
        </div>
      </section>

      {/* BLOQUE 2 - AUTORIDAD */}
      <section className="authority-section">
        <div className="authority-content">
          <div className="authority-item">
            <div className="authority-check">✓</div>
            <p>Más de 4000 mujeres asesoradas</p>
          </div>
          <div className="authority-item">
            <div className="authority-check">✓</div>
            <p>Atención personalizada en showroom privado</p>
          </div>
          <div className="authority-item">
            <div className="authority-check">✓</div>
            <p>Vestidos seleccionados y en excelente estado</p>
          </div>
        </div>
      </section>

      {/* BLOQUE 3 - GALERÍA ORGANIZADA POR CATEGORÍAS */}
      <section className="gallery-section">
        {/* Frase emocional antes de la galería */}
        <h2 className="gallery-main-title">Encontrá el vestido perfecto para tu evento</h2>
        
        {/* Sección Vestidos de Fiesta */}
        <div className="gallery-category">
          <h3 className="gallery-category-title">Vestidos de Fiesta</h3>
          <div className="gallery-grid-category">
            {galleryImages.fiesta.map((image, index) => (
              <div key={`fiesta-${index}`} className="gallery-item">
                <img
                  src={image}
                  alt={`Vestido de Fiesta SAORI ${index + 1}`}
                  className="gallery-image"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Sección Vestidos de Novia */}
        <div className="gallery-category">
          <h3 className="gallery-category-title">Vestidos de Novia</h3>
          <div className="gallery-grid-category">
            {galleryImages.novia.map((image, index) => (
              <div key={`novia-${index}`} className="gallery-item">
                <img
                  src={image}
                  alt={`Vestido de Novia SAORI ${index + 1}`}
                  className="gallery-image"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Sección Vestidos para XV */}
        <div className="gallery-category">
          <h3 className="gallery-category-title">Vestidos para XV</h3>
          <div className="gallery-grid-category">
            {galleryImages.xv.map((image, index) => (
              <div key={`xv-${index}`} className="gallery-item">
                <img
                  src={image}
                  alt={`Vestido para XV SAORI ${index + 1}`}
                  className="gallery-image"
                />
              </div>
            ))}
          </div>
        </div>

        {/* CTA adicional después de la galería */}
        <div className="gallery-cta-container">
          <Button
            onClick={handleWhatsAppClick}
            className="gallery-consult-btn"
            size="lg"
            data-testid="gallery-consult-whatsapp-btn">
            <MessageCircle className="mr-2 h-5 w-5" />
            <span className="btn-text-desktop">Consultar si este modelo está disponible</span>
            <span className="btn-text-mobile">Consultar</span>
          </Button>
          <Button
            onClick={handleVerMasModelos}
            variant="outline"
            className="gallery-see-more"
            data-testid="ver-mas-modelos-btn">
            <ExternalLink className="mr-2 h-4 w-4" />
            Ver más modelos
          </Button>
        </div>
      </section>

      {/* BLOQUE 4 - RANGO DE PRECIOS (MEJORADO) */}
      <section className="pricing-section">
        <div className="pricing-content">
          <h2 className="pricing-title">Alquileres desde $50.000</h2>
          <p className="pricing-subtitle">El valor final depende del modelo elegido y la fecha del evento</p>
          <div className="pricing-benefits">
            <p className="pricing-benefit-item">✔ Asesoramiento personalizado</p>
            <p className="pricing-benefit-item">✔ Prueba en showroom privado</p>
            <p className="pricing-benefit-item">✔ Vestidos importados, modelos únicos</p>
          </div>
          <Button
            onClick={handleWhatsAppClick}
            className="pricing-cta-button"
            size="lg"
            data-testid="pricing-whatsapp-btn">
            <MessageCircle className="mr-2 h-5 w-5" />
            Consultar disponibilidad
          </Button>
        </div>
      </section>

      {/* BLOQUE 5 - ESCASEZ */}
      <section className="scarcity-section">
        <div className="scarcity-content">
          <h3 className="scarcity-title">Septiembre y Octubre son los meses más solicitados</h3>
          <p className="scarcity-text">Las fechas se reservan por orden de confirmación</p>
        </div>
      </section>

      {/* BLOQUE 6 - CÓMO FUNCIONA */}
      <section className="process-section">
        <h2 className="section-title">¿Cómo funciona?</h2>
        <div className="process-grid">
          {steps.map((step, index) =>
            <div key={index} className="process-card">
              <div className="process-icon">
                {step.icon}
              </div>
              <h3 className="process-title">{step.title}</h3>
              <p className="process-description">{step.description}</p>
            </div>
          )}
        </div>
      </section>

      {/* BLOQUE 7 - TESTIMONIOS (RESEÑAS DE GOOGLE) */}
      <section className="testimonials-section">
        <h2 className="testimonials-title">Lo que dicen en Google</h2>
        <p className="testimonials-subtitle">Más de 100 reseñas reales de clientas</p>
        <div className="testimonials-grid">
          <div className="testimonial-card google-review">
            <div className="google-stars">
              <Star className="star-icon filled" />
              <Star className="star-icon filled" />
              <Star className="star-icon filled" />
              <Star className="star-icon filled" />
              <Star className="star-icon filled" />
            </div>
            <p className="testimonial-text">"Excelente atención, las chicas muy amables. Fui muy indecisa y me consiguieron el vestido perfecto. Muchas gracias! ❤️"</p>
            <p className="testimonial-author">— Marcela Sanchez</p>
          </div>
          <div className="testimonial-card google-review">
            <div className="google-stars">
              <Star className="star-icon filled" />
              <Star className="star-icon filled" />
              <Star className="star-icon filled" />
              <Star className="star-icon filled" />
              <Star className="star-icon filled" />
            </div>
            <p className="testimonial-text">"Gracias Saori por compartir este momento tan especial para nosotros 🥰 El vestido hermoso, se lució bien y el calzado también. Todo excelente, gracias totales."</p>
            <p className="testimonial-author">— Débora Geraldina Cauchois</p>
          </div>
          <div className="testimonial-card google-review">
            <div className="google-stars">
              <Star className="star-icon filled" />
              <Star className="star-icon filled" />
              <Star className="star-icon filled" />
              <Star className="star-icon filled" />
              <Star className="star-icon filled" />
            </div>
            <p className="testimonial-text">"Excelente trato, minuciosa, detallista, tienen un ojo más allá para recomendar qué le queda mejor a la clienta. 10 de 10."</p>
            <p className="testimonial-author">— Pamela Ponce</p>
          </div>
          <div className="testimonial-card google-review">
            <div className="google-stars">
              <Star className="star-icon filled" />
              <Star className="star-icon filled" />
              <Star className="star-icon filled" />
              <Star className="star-icon filled" />
              <Star className="star-icon filled" />
            </div>
            <p className="testimonial-text">"Excelente atención. Encontré lo que buscaba y era mi talle, que siempre me cuesta encontrar. Súper feliz con mi look. Me asesoraron y me tuvieron paciencia. Práctico el alquiler, sin vueltas. Muy buen precio también. Será mi solución para futuros eventos. Gracias chicas! Súper recomendable."</p>
            <p className="testimonial-author">— Jimena Baez</p>
          </div>
        </div>
        <p className="testimonials-note">Podés ver todas nuestras reseñas en Google</p>
      </section>

      {/* BLOQUE 8 - FORMULARIO DE RESERVA */}
      <section className="form-section">
        <div className="form-container">
          <h2 className="form-title">Verificá disponibilidad para tu fecha</h2>
          <p className="form-subtitle">Te respondemos en breve para coordinar tu cita</p>
          <ReservationForm />
        </div>
        {/* Mensaje de seguridad debajo del formulario */}
        <div className="security-message">
          <p className="security-text">Citas sin compromiso de reserva</p>
          <p className="security-text">Elegí con tranquilidad</p>
        </div>
      </section>

      {/* BLOQUE 9 - CTA FINAL (ACTUALIZADO) */}
      <section className="cta-final-section">
        <div className="cta-final-content">
          <h2 className="cta-final-title">¿Tenés un evento este año o en 2026?</h2>
          <p className="cta-final-subtitle">No esperes a último momento</p>
          <p className="cta-final-refuerzo">
            Novias y XV suelen reservar con 6 meses de anticipación<br/>
            Vestidos de fiesta con 2 meses
          </p>
          <Button
            onClick={handleWhatsAppClick}
            className="cta-final-button"
            size="lg"
            data-testid="cta-final-whatsapp-btn">
            <MessageCircle className="mr-2 h-5 w-5" />
            Escribinos ahora
          </Button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="footer-content">
          <div className="social-links">
            <p className="social-title">Seguinos en</p>
            <div className="social-icons">
              <a
                href="https://www.instagram.com/saorivestidos/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label="Instagram">
                <Instagram className="w-6 h-6" />
              </a>
              <a
                href="https://www.facebook.com/saorivestidos?locale=es_LA"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label="Facebook">
                <Facebook className="w-6 h-6" />
              </a>
              <a
                href="https://www.tiktok.com/@saorivestidos"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label="TikTok">
                <img 
                  src="https://customer-assets.emergentagent.com/job_saori-boutique/artifacts/b7j1nnm6_icono%20tiktok.png"
                  alt="TikTok"
                  className="social-icon-img"
                />
              </a>
            </div>
          </div>
          <p className="footer-text">SAORI © 2025</p>
        </div>
      </footer>

      {/* BOTÓN FLOTANTE WHATSAPP */}
      <button
        onClick={handleWhatsAppClick}
        className="whatsapp-float"
        aria-label="Contactar por WhatsApp"
        data-testid="whatsapp-float-btn">
        <img 
          src="https://customer-assets.emergentagent.com/job_saori-boutique/artifacts/zfjquiqz_icono%20wsp.png"
          alt="WhatsApp"
          className="whatsapp-icon-img"
        />
      </button>
    </div>
  );
};

export default Home;
